import { getPageViewportParams } from "./pdfPageViewport";
import type { PdfZoomState } from "./pdfZoom";
import { renderTextLayer } from "./pdfTextLayer";

export type PdfPageRenderRefs = {
  viewerScrollRef: React.MutableRefObject<HTMLDivElement | null>;
  pageCanvasRefs: React.MutableRefObject<{ [page: number]: HTMLCanvasElement | null }>;
  pageTextLayerRefs: React.MutableRefObject<{ [page: number]: HTMLDivElement | null }>;
  pageWrapRefs: React.MutableRefObject<{ [page: number]: HTMLDivElement | null }>;
  textLayerTaskRefs: React.MutableRefObject<{ [page: number]: { cancel?: () => void } | null }>;
  textContentByPageRef: React.MutableRefObject<{ [page: number]: any[] }>;
  textDivsByPageRef: React.MutableRefObject<{ [page: number]: HTMLElement[] }>;
  renderTaskRef: React.MutableRefObject<any>;
};

type RenderPdfPagesOptions = {
  pdfDoc: any;
  pageNumbers: number[];
  pdfZoom: PdfZoomState;
  currentAngle: number;
  viewerWidth: number;
  viewerHeight: number;
  refs: PdfPageRenderRefs;
  isCancelled: () => boolean;
  optionalContentConfigPromise?: Promise<any>;
  onPageRendered?: (pageNo: number) => void;
};

export const renderPdfPages = async (options: RenderPdfPagesOptions) => {
  const {
    pdfDoc,
    pageNumbers,
    pdfZoom,
    currentAngle,
    viewerWidth,
    viewerHeight,
    refs,
    isCancelled,
    onPageRendered
  } = options;

  const optionalContentConfigPromise =
    options.optionalContentConfigPromise ||
    pdfDoc.getOptionalContentConfig({ intent: "any" });

  for (const pageNo of pageNumbers) {
    if (isCancelled()) break;
    const canvas = refs.pageCanvasRefs.current[pageNo];
    if (!canvas) continue;

    const page = await pdfDoc.getPage(pageNo);
    if (isCancelled()) break;

    const liveViewerWidth = refs.viewerScrollRef.current?.clientWidth || viewerWidth || 0;
    const liveViewerHeight = refs.viewerScrollRef.current?.clientHeight || viewerHeight || 0;
    const params = getPageViewportParams(
      page,
      pdfZoom,
      liveViewerWidth,
      liveViewerHeight,
      currentAngle
    );
    const viewport = page.getViewport({
      scale: params.effectiveScale * params.pixelRatio,
      rotation: params.effectiveRotation
    });
    const textViewport = page.getViewport({
      scale: params.effectiveScale,
      rotation: params.effectiveRotation
    });
    const context = canvas.getContext("2d");
    if (!context) continue;

    context.setTransform(1, 0, 0, 1, 0, 0);
    const { cssWidth, cssHeight } = params;
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    context.fillStyle = "rgb(255, 255, 255)";
    context.fillRect(0, 0, canvas.width, canvas.height);

    refs.renderTaskRef.current?.cancel?.();
    refs.renderTaskRef.current = page.render({
      canvasContext: context,
      viewport,
      intent: "any",
      background: "rgb(255, 255, 255)",
      optionalContentConfigPromise
    });
    await refs.renderTaskRef.current.promise;
    if (isCancelled()) break;

    const textContent = await page.getTextContent();
    refs.textContentByPageRef.current[pageNo] = textContent.items;
    const pageWrap = refs.pageWrapRefs.current[pageNo];
    const textLayerEl = refs.pageTextLayerRefs.current[pageNo];
    if (pageWrap) {
      pageWrap.style.width = `${cssWidth}px`;
      pageWrap.style.height = `${cssHeight}px`;
    }
    if (textLayerEl) {
      refs.textLayerTaskRefs.current[pageNo]?.cancel?.();
      const textLayerTask = await renderTextLayer(textLayerEl, page, textViewport, textContent);
      refs.textLayerTaskRefs.current[pageNo] = textLayerTask;
      refs.textDivsByPageRef.current[pageNo] = textLayerTask?.textDivs || [];
    }

    onPageRendered?.(pageNo);
  }
};

export const prefetchPageDimensions = async (options: {
  pdfDoc: any;
  totalPages: number;
  pdfZoom: PdfZoomState;
  currentAngle: number;
  viewerWidth: number;
  viewerHeight: number;
  isCancelled: () => boolean;
  onBatch: (dimensions: Record<number, { width: number; height: number }>) => void;
}) => {
  const {
    pdfDoc,
    totalPages,
    pdfZoom,
    currentAngle,
    viewerWidth,
    viewerHeight,
    isCancelled,
    onBatch
  } = options;

  const dimensions: Record<number, { width: number; height: number }> = {};
  const batchSize = 20;

  for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
    if (isCancelled()) return;
    const page = await pdfDoc.getPage(pageNo);
    const params = getPageViewportParams(page, pdfZoom, viewerWidth, viewerHeight, currentAngle);
    dimensions[pageNo] = { width: params.cssWidth, height: params.cssHeight };
    if (pageNo % batchSize === 0 || pageNo === totalPages) {
      onBatch({ ...dimensions });
    }
  }
};
